import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import ThemeToggle from "@/components/layout/ThemeToggle";
import NotificationsBell from "@/components/layout/NotificationsBell";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { NAV } from "@/constants/testIds";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid={NAV.logo}>
          <img src="/logo.png" alt="Kalypso Music" className="h-9 w-9 rounded-xl object-cover" />
          <span className="font-heading text-lg font-semibold tracking-tight">Kalypso Music</span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          <Link to="/" data-testid="nav-home" className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            {t("nav.home")}
          </Link>
          <Link to="/services" data-testid="nav-services" className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            Services
          </Link>
          <Link to="/create" data-testid={NAV.create} className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            {t("nav.create")}
          </Link>
          {user && (
            <Link to="/dashboard" data-testid={NAV.dashboard} className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("nav.dashboard")}
            </Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid={NAV.admin} className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
              {t("nav.admin")}
            </Link>
          )}
        </div>

        <div className="flex items-center gap-1">
          <LanguageSwitcher />
          <ThemeToggle />
          {user && <NotificationsBell />}
          {user ? (
            <Button variant="outline" size="sm" className="ml-2 hidden rounded-full md:inline-flex" data-testid={NAV.logout} onClick={handleLogout}>
              {t("nav.logout")}
            </Button>
          ) : (
            <Button size="sm" className="ml-2 hidden rounded-full md:inline-flex" data-testid={NAV.login} onClick={() => navigate("/login")}>
              {t("nav.login")}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="md:hidden" data-testid={NAV.mobileMenuBtn} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="flex flex-col gap-1 border-t border-border p-4 md:hidden">
          <Link to="/" data-testid="nav-home-mobile" className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent/10" onClick={() => setMobileOpen(false)}>
            {t("nav.home")}
          </Link>
          <Link to="/services" data-testid="nav-services-mobile" className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent/10" onClick={() => setMobileOpen(false)}>
            Services
          </Link>
          <Link to="/create" className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent/10" onClick={() => setMobileOpen(false)}>
            {t("nav.create")}
          </Link>
          {user && (
            <Link to="/dashboard" className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent/10" onClick={() => setMobileOpen(false)}>
              {t("nav.dashboard")}
            </Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent/10" onClick={() => setMobileOpen(false)}>
              {t("nav.admin")}
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="rounded-lg px-3 py-2 text-left text-sm font-medium hover:bg-accent/10">
              {t("nav.logout")}
            </button>
          ) : (
            <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent/10" onClick={() => setMobileOpen(false)}>
              {t("nav.login")}
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
